import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, EyeOff, Trash2 } from "lucide-react";

import { DashboardShell } from "@/components/DashboardShell";
import { ADMIN_NAV } from "@/lib/nav";
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import {
  REVIEW_SORTS,
  REVIEW_SORT_LABEL,
  deleteReview,
  listAllReviews,
  setReviewHidden,
  sortReviews,
  summarize,
  type ReviewSort,
} from "@/lib/reviews";

type Tab = "all" | "reported" | "hidden";

export const Route = createFileRoute("/_authenticated/admin/reviews")({
  head: () => ({
    meta: [
      { title: "Review Moderation · Landoo Admin" },
      {
        name: "description",
        content:
          "Moderate Landoo customer reviews: hide inappropriate content and delete reported reviews.",
      },
      { property: "og:title", content: "Review Moderation · Landoo Admin" },
      {
        property: "og:description",
        content:
          "Moderate Landoo customer reviews: hide inappropriate content and delete reported reviews.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminReviewsPage,
});

function AdminReviewsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("all");
  const [sort, setSort] = useState<ReviewSort>("latest");
  const [search, setSearch] = useState("");
  const [reasonFor, setReasonFor] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin", "reviews"],
    queryFn: listAllReviews,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "reviews"] });

  const summary = useMemo(() => summarize(rows.filter((r) => !r.is_hidden)), [rows]);

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (tab === "reported" && r.report_count < 1) return false;
      if (tab === "hidden" && !r.is_hidden) return false;
      if (!q) return true;
      return `${r.listing_title ?? ""} ${r.customer_name ?? ""} ${r.title ?? ""} ${r.body ?? ""}`
        .toLowerCase()
        .includes(q);
    });
    return sortReviews(filtered, sort);
  }, [rows, tab, sort, search]);

  const hide = useMutation({
    mutationFn: ({ id, hidden, why }: { id: string; hidden: boolean; why: string }) =>
      setReviewHidden(id, hidden, why, user!.id),
    onSuccess: () => {
      toast.success("Review visibility updated.");
      setReasonFor(null);
      setReason("");
      refresh();
    },
    onError: () => toast.error("Could not update the review."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteReview(id),
    onSuccess: () => {
      toast.success("Review deleted.");
      refresh();
    },
    onError: () => toast.error("Could not delete the review."),
  });

  return (
    <DashboardShell title="Reviews & Ratings" role="Admin" items={ADMIN_NAV}>
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Average Rating" value={summary.average ? `${summary.average} / 5` : "—"} />
        <Stat label="Total Reviews" value={String(rows.length)} />
        <Stat label="Reported" value={String(rows.filter((r) => r.report_count > 0).length)} />
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-1.5">
          {(["all", "reported", "hidden"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium capitalize transition ${
                tab === t ? "bg-brown-dark text-white" : "bg-secondary text-brown-dark"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            className="sm:w-56"
            placeholder="Search listing, customer, text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select value={sort} onValueChange={(v) => setSort(v as ReviewSort)}>
            <SelectTrigger className="sm:w-44" aria-label="Sort reviews">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REVIEW_SORTS.map((s) => (
                <SelectItem key={s} value={s}>
                  {REVIEW_SORT_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading reviews…</p>
      ) : list.length === 0 ? (
        <p className="mt-6 rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No reviews in this view.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {list.map((r) => (
            <li key={r.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <StarRating value={r.rating} size="sm" />
                  <p className="mt-1 text-sm font-semibold text-brown-dark">
                    {r.listing_title ?? "Listing"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {r.customer_name ?? "Customer"} ·{" "}
                    {new Date(r.created_at).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                    {r.report_count > 0 ? ` · ${r.report_count} report(s)` : ""}
                    {r.is_hidden ? " · Hidden" : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {r.is_hidden ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => hide.mutate({ id: r.id, hidden: false, why: "" })}
                    >
                      <Eye className="mr-1 h-3.5 w-3.5" /> Unhide
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setReasonFor(r.id);
                        setReason("");
                      }}
                    >
                      <EyeOff className="mr-1 h-3.5 w-3.5" /> Hide
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove.mutate(r.id)}>
                    <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                  </Button>
                </div>
              </div>
              {r.title && <p className="mt-2 text-sm font-medium text-brown-dark">{r.title}</p>}
              {r.body && (
                <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{r.body}</p>
              )}
              {r.hidden_reason && (
                <p className="mt-2 text-xs text-destructive">Hidden reason: {r.hidden_reason}</p>
              )}

              {reasonFor === r.id && (
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <Input
                    placeholder="Reason for hiding"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => hide.mutate({ id: r.id, hidden: true, why: reason })}
                    >
                      Confirm hide
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setReasonFor(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </DashboardShell>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold text-brown-dark">{value}</p>
    </div>
  );
}
